import type { UsuarioAtual } from '@mymaxx/shared';
import { TAMANHO_MAXIMO_AVATAR, TIPOS_AVATAR_ACEITOS } from '@mymaxx/shared';
import {
  Camera,
  KeyRound,
  ListChecks,
  LogOut,
  ShieldCheck,
  Trash2,
  UserCog,
  Volume2,
  VolumeX,
  Bell,
  BellOff,
  Zap,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { authApi, ErroApi } from '../lib/api';
import { useSessao } from '../lib/sessao';
import { avisoLigado, desligarAviso, ligarAviso, suportaAviso } from '../lib/push';
import { definirSom, somLigado } from '../lib/som';
import { MeuDia } from './MeuDia';
import { MinhasRespostas } from './MinhasRespostas';

/** Iniciais como retrato reserva — nome curto vira uma letra só. */
export function iniciaisDe(nome: string): string {
  const partes = nome.trim().split(/\s+/);
  return (
    (partes[0]?.[0] ?? '') + (partes.length > 1 ? (partes.at(-1)?.[0] ?? '') : '')
  ).toUpperCase();
}

export function Avatar({
  usuario,
  tamanho = 36,
}: {
  usuario: UsuarioAtual | null;
  tamanho?: number;
}) {
  if (usuario?.avatarUrl) {
    return (
      <img
        src={usuario.avatarUrl}
        alt=""
        style={{ width: tamanho, height: tamanho }}
        className="shrink-0 rounded-full object-cover"
      />
    );
  }
  return (
    <span
      style={{ width: tamanho, height: tamanho, fontSize: tamanho * 0.36 }}
      className="flex shrink-0 items-center justify-center rounded-full bg-marca-600 font-medium text-white"
      aria-hidden
    >
      {iniciaisDe(usuario?.nome ?? '')}
    </span>
  );
}

/**
 * Tudo que é da própria pessoa mora atrás da foto: trocar o retrato, a senha, a
 * verificação em duas etapas e derrubar as sessões. Antes isso ficava num link solto
 * no rodapé; agora está onde a pessoa procura (decisão de 24/09).
 */
export function MenuPerfil({
  aoSair,
  aoVerMeuDia,
}: {
  aoSair: () => void;
  /**
   * Quem já mostra "meu dia" em outro lugar assume a abertura — é o caso da estação, em
   * que o resumo do dia fica à vista e clicar nele abre a lista. Sem isto, a mesma janela
   * teria dois donos e dois estados.
   */
  aoVerMeuDia?: () => void;
}) {
  const [verMeuDia, setVerMeuDia] = useState(false);
  const [comSom, setComSom] = useState(somLigado);
  const [comAviso, setComAviso] = useState(avisoLigado);
  const [avisoErro, setAvisoErro] = useState<string | null>(null);
  const [verRespostas, setVerRespostas] = useState(false);
  const { usuario, limpar } = useSessao();
  const navegar = useNavigate();
  const [aberto, setAberto] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const arquivo = useRef<HTMLInputElement>(null);
  const caixa = useRef<HTMLDivElement>(null);

  // Clicar fora fecha, como qualquer menu.
  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  /**
   * Liga e desliga o aviso do sistema, dizendo a verdade quando não dá.
   *
   * Permissão negada no navegador não se desfaz por código: o atendente precisa reabri-la
   * no cadeado da barra de endereço, e é isso que o recado diz.
   */
  async function alternarAviso() {
    setAvisoErro(null);
    if (comAviso) {
      await desligarAviso();
      setComAviso(false);
      return;
    }
    const resultado = await ligarAviso();
    setComAviso(resultado === 'ligado');
    if (resultado === 'negado') {
      setAvisoErro(
        'O navegador está bloqueando os avisos. Libere no cadeado, ao lado do endereço.',
      );
    } else if (resultado === 'indisponivel') {
      setAvisoErro('Não consegui ligar o aviso agora. Tente de novo em instantes.');
    }
  }

  async function trocarFoto(arq: File) {
    setErro(null);
    if (!TIPOS_AVATAR_ACEITOS.includes(arq.type)) {
      setErro('A foto precisa ser JPEG, PNG ou WebP.');
      return;
    }
    if (arq.size > TAMANHO_MAXIMO_AVATAR) {
      setErro('A foto passou de 2 MB.');
      return;
    }
    setEnviando(true);
    try {
      const atualizado = await authApi.trocarFoto(arq);
      useSessao.setState({ usuario: atualizado });
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui trocar a foto.');
    } finally {
      setEnviando(false);
    }
  }

  async function removerFoto() {
    setErro(null);
    try {
      useSessao.setState({ usuario: await authApi.removerFoto() });
    } catch {
      setErro('Não consegui remover a foto.');
    }
  }

  async function sairDeTodos() {
    if (!confirm('Encerrar a sessão em todos os dispositivos, inclusive este?')) return;
    try {
      await authApi.logoutTodos();
    } finally {
      limpar();
      navegar('/login', { replace: true });
    }
  }

  const ir = (para: string) => {
    setAberto(false);
    navegar(para);
  };

  return (
    <div ref={caixa} className="relative">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={aberto}
        className="flex w-full items-center gap-2 rounded-lg p-1 text-left transition hover:bg-neutral-100 dark:hover:bg-neutral-800"
      >
        <Avatar usuario={usuario} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{usuario?.nome}</span>
          <span className="block truncate text-xs text-neutral-500">{usuario?.perfil.nome}</span>
        </span>
      </button>

      {aberto && (
        <div
          role="menu"
          className="absolute top-full left-0 z-30 mt-1 w-64 rounded-xl border border-neutral-200 bg-white p-1.5 shadow-xl dark:border-neutral-700 dark:bg-neutral-900"
        >
          <div className="flex items-center gap-2 px-2 py-2">
            <Avatar usuario={usuario} tamanho={44} />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{usuario?.nome}</p>
              <p className="truncate text-xs text-neutral-500">{usuario?.email}</p>
            </div>
          </div>

          <input
            ref={arquivo}
            type="file"
            accept={TIPOS_AVATAR_ACEITOS.join(',')}
            className="hidden"
            onChange={(e) => {
              const arq = e.target.files?.[0];
              e.target.value = '';
              if (arq) void trocarFoto(arq);
            }}
          />

          {erro && (
            <p className="px-2 pb-1 text-[11px] text-red-600 dark:text-red-400" role="alert">
              {erro}
            </p>
          )}

          <hr className="my-1 border-neutral-200 dark:border-neutral-700" />

          <ItemMenu
            Icone={Camera}
            rotulo={enviando ? 'Enviando foto…' : 'Trocar foto de perfil'}
            onClick={() => arquivo.current?.click()}
          />
          {usuario?.avatarUrl && (
            <ItemMenu Icone={Trash2} rotulo="Remover foto" onClick={() => void removerFoto()} />
          )}
          <ItemMenu Icone={KeyRound} rotulo="Alterar senha" onClick={() => ir('/minha-conta')} />
          <ItemMenu
            Icone={ShieldCheck}
            rotulo="Verificação em duas etapas"
            onClick={() => ir('/minha-conta')}
          />
          <ItemMenu Icone={UserCog} rotulo="Minha conta" onClick={() => ir('/minha-conta')} />

          <hr className="my-1 border-neutral-200 dark:border-neutral-700" />

          <ItemMenu
            Icone={ListChecks}
            rotulo="Meus atendimentos de hoje"
            onClick={() => {
              setAberto(false);
              if (aoVerMeuDia) aoVerMeuDia();
              else setVerMeuDia(true);
            }}
          />
          <ItemMenu
            Icone={Zap}
            rotulo="Minhas respostas rápidas"
            onClick={() => {
              setAberto(false);
              setVerRespostas(true);
            }}
          />
          {/* O toque de mensagem nova é escolha de cada um: sala cheia de atendentes com o
              som ligado vira sino. Fica aqui porque é preferência da pessoa, não da conta. */}
          <ItemMenu
            Icone={comSom ? Volume2 : VolumeX}
            rotulo={comSom ? 'Som de mensagem nova: ligado' : 'Som de mensagem nova: desligado'}
            onClick={() => {
              definirSom(!comSom);
              setComSom(!comSom);
            }}
          />
          {/* O aviso do sistema cobre o que o som não alcança: painel minimizado ou atrás de
              outra janela. Começa desligado porque ligar pede permissão ao navegador, e
              permissão pedida sem a pessoa esperar costuma ser negada para sempre. */}
          {suportaAviso() && (
            <ItemMenu
              Icone={comAviso ? Bell : BellOff}
              rotulo={comAviso ? 'Aviso no computador: ligado' : 'Aviso no computador: desligado'}
              onClick={() => void alternarAviso()}
            />
          )}
          {avisoErro && <p className="px-3 pb-1 text-[11px] text-amber-600">{avisoErro}</p>}

          <hr className="my-1 border-neutral-200 dark:border-neutral-700" />

          <ItemMenu
            Icone={LogOut}
            rotulo="Sair de todos os dispositivos"
            onClick={() => void sairDeTodos()}
          />
          <ItemMenu Icone={LogOut} rotulo="Sair" onClick={aoSair} />
        </div>
      )}

      {verMeuDia && <MeuDia onFechar={() => setVerMeuDia(false)} />}
      {verRespostas && <MinhasRespostas onFechar={() => setVerRespostas(false)} />}
    </div>
  );
}

function ItemMenu({
  Icone,
  rotulo,
  onClick,
}: {
  Icone: typeof Camera;
  rotulo: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-neutral-700 transition hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-800"
    >
      <Icone size={15} className="shrink-0 text-neutral-500" /> {rotulo}
    </button>
  );
}
